import { api } from ".";
import {
  SpatialDataset,
  SpatialDatasetCreatePayload,
  SpatialDatasetUpdatePayload,
  SpatialDatasetProcessingStatus,
  PaginatedResponse
} from "../types";

// ─── Response envelope helpers ───────────────────────────────────────────────
// POST /spatial-datasets/ and PATCH /spatial-datasets/{id}/ wrap the body in
// { message, data: {...} }. GET /spatial-datasets/{id}/ returns bare objects.
// See SPATIAL_DATASET_FRONTEND.md §2 for the full envelope table.

interface WrappedResponse<T> {
  message: string;
  data: T;
}

// ─── List query params ───────────────────────────────────────────────────────

export interface SpatialDatasetListParams {
  dataset_type?: "raster" | "vector" | "tabular";
  processing_status?: SpatialDatasetProcessingStatus;
  search?: string;
  bbox?: string; // "minLng,minLat,maxLng,maxLat"
  time_series_group_id?: string;
  ordering?: string;
  include_relations?: boolean;
  page?: number;
  page_size?: number;
}

// ─── Multipart types ─────────────────────────────────────────────────────────

export interface MultipartInitializePayload {
  filename: string;
  parts: number;
  fileSize: number;
  fileType: string;
}

export interface MultipartInitializeResponse {
  upload_id: string;
  urls: string[];
  calculation: Record<string, any>;
}

export interface MultipartPart {
  PartNumber: number;
  ETag: string;
}

export interface MultipartCompleteResponse {
  detail: string;
  location: string;
  etag: string;
  processing_triggered: boolean;
}

/**
 * Response from POST /api/v2/spatial-datasets/multipart/abort/
 *
 * The backend now deletes the SpatialDataset row as part of aborting the
 * multipart upload (it already knows the dataset_id from the upload session).
 * dataset_deleted: true means the DB record is gone — treat it as such in the
 * UI (don't try to re-fetch or PATCH the record after this call).
 */
export interface MultipartAbortResponse {
  detail: string;
  dataset_id?: number;
  dataset_deleted?: boolean;
}

export interface ProcessResponse {
  processing_triggered: boolean;
  processing_status: SpatialDatasetProcessingStatus;
}

export interface DownloadResponse {
  url: string;
  filename: string;
  expires_in: number;
}

// ─── CRUD ────────────────────────────────────────────────────────────────────

// POST returns { message, data: {...} } — read res.data.data (see §2)
export const createSpatialDataset = async (payload: SpatialDatasetCreatePayload): Promise<SpatialDataset> => {
  const res = await api.post<WrappedResponse<SpatialDataset>>("api/v2/spatial-datasets/", payload);
  return res.data.data;
};

export const listSpatialDatasets = async (params?: SpatialDatasetListParams): Promise<PaginatedResponse<SpatialDataset>> => {
  const res = await api.get<PaginatedResponse<SpatialDataset>>("api/v2/spatial-datasets/", { params });
  return res.data;
};

// GET /{id}/ returns a bare object — no `data` wrapper (see §2)
export const getSpatialDataset = async (id: number, params?: { include_relations?: boolean }): Promise<SpatialDataset> => {
  const res = await api.get<SpatialDataset>(`api/v2/spatial-datasets/${id}/`, { params });
  return res.data;
};

// PATCH /{id}/ wraps the response in { message, data: {...} }
export const updateSpatialDataset = async (id: number, payload: SpatialDatasetUpdatePayload): Promise<SpatialDataset> => {
  const res = await api.patch<WrappedResponse<SpatialDataset>>(`api/v2/spatial-datasets/${id}/`, payload);
  return res.data.data;
};

export const deleteSpatialDataset = async (id: number): Promise<void> => {
  await api.delete(`api/v2/spatial-datasets/${id}/`);
};

// ─── Upload flow ─────────────────────────────────────────────────────────────
// The frontend always uses multipart upload (SPATIAL_DATASET_FRONTEND (2).md §0 §7).
// sign/ exists on the backend but is NOT used by the frontend.
// processSpatialDataset() is kept for re-running failed conversions from SpatialDatasetDetail.

// rasterOutput only applies to geotiff — ignored for vectors
export const processSpatialDataset = async (id: number, rasterOutput?: "cog" | "pmtiles"): Promise<ProcessResponse> => {
  const payload = rasterOutput ? { raster_output: rasterOutput } : {};
  const res = await api.post<ProcessResponse>(`api/v2/spatial-datasets/${id}/process/`, payload);
  return res.data;
};

// ─── Multipart upload ────────────────────────────────────────────────────────
// Chunk size must be exactly 10 MB (backend rejects mismatched part count).
// complete/ auto-triggers conversion using raster_output set at create time — no process/ call.
// urls array has `parts + 2` entries — use only the first `parts` (spares for retries).

export const initializeMultipartUpload = async (id: number, payload: MultipartInitializePayload): Promise<MultipartInitializeResponse> => {
  const res = await api.post<MultipartInitializeResponse>(`api/v2/spatial-datasets/${id}/multipart/initialize/`, payload);
  return res.data;
};

export const completeMultipartUpload = async (uploadId: string, parts: MultipartPart[]): Promise<MultipartCompleteResponse> => {
  const res = await api.post<MultipartCompleteResponse>("api/v2/spatial-datasets/multipart/complete/", { upload_id: uploadId, parts });
  return res.data;
};

export const abortMultipartUpload = async (uploadId: string): Promise<MultipartAbortResponse> => {
  const res = await api.post<MultipartAbortResponse>("api/v2/spatial-datasets/multipart/abort/", {
    upload_id: uploadId
  });
  return res.data;
};

// ─── Download ─────────────────────────────────────────────────────────────────

export const getSpatialDatasetDownloadUrl = async (id: number): Promise<DownloadResponse> => {
  const res = await api.get<DownloadResponse>(`api/v2/spatial-datasets/${id}/download/`);
  return res.data;
};

// ─── QGIS Export ──────────────────────────────────────────────────────────────
// GET /api/v2/spatial-datasets/{id}/qgis-export/
// Returns 200 as a PLAIN body (no envelope) — URL is at top-level `url`, NOT
// under `data`. This is intentional — the endpoint follows the same convention
// as its neighbour GET /spatial-datasets/{id}/download/.
// Lifetime: 8 h, NOT revocable (CloudFront signed URL — self-contained credential).
// See docs/developer-guides/QGIS_EXPORT_FRONTEND_CONTRACT.md §3.

/** One band of the COG. `color_interp` and `stats` exist for rasters converted since 28 Sep 2026. */
export interface SpatialDatasetBand {
  name: string;
  data_type: string;
  nodata: number | null;
  /** GDAL colour interpretation: "red", "green", "blue", "gray", "alpha", "palette", "undefined", … */
  color_interp?: string;
  /** Approximate min/max; null for an alpha band. */
  stats?: { min: number | null; max: number | null } | null;
}

/** How a raster file stores transparency. */
export type SpatialRasterMask = "per_dataset" | "alpha_band" | "nodata" | "none";

/**
 * `qgis-export`'s `raster` block: how the worker laid out the COG, and what the
 * original upload looks like. Null for rasters converted before 28 Sep 2026 —
 * re-processing fills it in.
 */
export interface SpatialDatasetRasterLayout {
  cog: {
    /** "rgb8": display-ready 8-bit, drawn byte-for-byte by the web map. "raw": data values. */
    encoding: "rgb8" | "raw" | null;
    mask: SpatialRasterMask | null;
    /** The band QGIS uses as alpha when it opens the COG: N+1 for an internal mask, the alpha band's index, or null. */
    qgis_alpha_band: number | null;
  };
  /** The ORIGINAL upload, which an offline `.qml` is applied to. */
  source: {
    bands: Array<{ data_type: string; color_interp: string; nodata: number | null }>;
    paletted: boolean;
    /** The linear stretch a single-band original's grey display COG was baked with. */
    display_stretch: { band: number; min: number | null; max: number | null; method: string } | null;
    /** Not sent yet (backend B-1). Same meaning as on `cog`, for the original. */
    mask?: SpatialRasterMask | null;
    qgis_alpha_band?: number | null;
  } | null;
}

/** The live raster layer with the web map's styling painted in: a GDAL VRT over the signed COG. */
export interface SpatialDatasetQgisStyled {
  supported: boolean;
  /** A GDAL VRT document — the `.qlr` datasource as-is. Expires with `qgis.datasource`. */
  datasource: string | null;
  /** The VRT's alpha band (the COG's mask × opacity): 4. */
  alpha_band: number | null;
  /** The MapLibre paint that was painted in. */
  paint: Record<string, number> | null;
  /** Why it is unsupported: no COG, a raw-value COG, or converted before the metadata existed. */
  reason: string | null;
}

export interface SpatialDatasetQgisExportResponse {
  url: string; // one-off signed DISPLAY url (CloudFront) → legacy no-key .qlr <datasource>
  display_type: "pmtiles" | "cog" | "parquet";
  dataset_type: "vector" | "raster" | "tabular";
  display_style: Record<string, unknown>; // same as SpatialDataset.display_style
  geometry_type: "point" | "line" | "polygon" | null; // null for rasters
  attribute_schema: Array<{ name: string; type: string }>;
  bands: SpatialDatasetBand[] | null;
  bbox: [number, number, number, number] | null;
  source_epsg: string | null;
  /** CRS of the converted display artifact. Use for QGIS, never source_epsg. */
  display_crs: string;
  qgis_supported: boolean;
  expires_in: number; // seconds (28800 = 8 h)
  expires_at: string; // ISO-8601 UTC
  /**
   * OAuth-only URLs — a `.qlr` cannot carry an OAuth session, so none of these
   * work in QGIS. Kept in the type only because the backend still sends it; do
   * not read it.
   */
  token_datasources: {
    original: string;
    display: string;
    analysis: string | null;
    gpkg: string | null;
  };
  /** Which analysis artifact exists, if any. Null means "not generated yet" — re-process. */
  analysis_type: "cog" | "geoparquet" | null;
  derivatives: { cog: boolean; pmtiles: boolean; geoparquet: boolean; gpkg: boolean };
  /**
   * The live `.qlr` datasource. Signed CloudFront URLs, already wrapped in
   * `/vsicurl/` — a COG for rasters, GeoParquet (or GPKG) for vectors. Expires
   * with `expires_at`.
   */
  qgis?: {
    supported: boolean;
    provider: "xyzvectortiles" | "gdal" | "ogr" | "wms" | null;
    datasource: string | null;
    /** Which artifact `datasource` points at. A GPKG datasource needs `|layername=layer` appended. */
    artifact: "cog" | "geoparquet" | "gpkg" | "pmtiles" | null;
    /** EPSG:3857 for rasters (reprojected), EPSG:4326 for vectors. */
    crs: string | null;
    reason: string | null;
    /** The artifact actually backing `datasource`; null when unsupported. */
    preferred: "geoparquet" | "gpkg" | "cog" | null;
  };
  /**
   * Signed no-key vector datasources by artifact, `/vsicurl/`-wrapped. Both
   * present lets the user pick GPKG — official macOS QGIS has no Parquet
   * driver. `{}` for rasters, tabular, or no derivative.
   */
  qgis_vector_sources: Partial<Record<"geoparquet" | "gpkg", string>>;
  /** Rasters only. Null for vectors and for rasters converted before 28 Sep 2026. */
  raster?: SpatialDatasetRasterLayout | null;
  /** Rasters only. */
  qgis_styled?: SpatialDatasetQgisStyled | null;
}

export const getSpatialDatasetQgisExport = async (id: number): Promise<SpatialDatasetQgisExportResponse> => {
  const res = await api.get<SpatialDatasetQgisExportResponse>(`api/v2/spatial-datasets/${id}/qgis-export/`);
  return res.data;
};

// ─── Tabular CSV rows (read-only in-app viewer) ──────────────────────────────
// GET /api/v2/csv-data/?file_key=<original_file_key>&limit=&offset= returns the
// CSV parsed to JSON rows. Uses the normal login Bearer (not a data-access token).
// The endpoint path is config-driven (config.tabularData.csvData) so it is passed
// in. The response envelope is not formally documented yet — callers normalise it
// defensively (see useCsvData).

export interface CsvDataParams {
  file_key: string;
  limit: number;
  offset: number;
}

export const getCsvData = async (endpoint: string, params: CsvDataParams): Promise<unknown> => {
  // The axios `api` instance already has REACT_APP_API_ROOT as baseURL, so strip a
  // leading slash from the (possibly config-provided) endpoint path.
  const path = endpoint.replace(/^\/+/, "");
  const res = await api.get(path, { params });
  return res.data;
};
